import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  LoadingState,
  ErrorState,
  EmptyState,
  StrengthBar,
  AttentionBadge,
  AskAboutThis,
  directionInfo,
  conflictKind,
  marginBand,
  humanise,
} from "@/components/research-hub";
import {
  useCioOpenConflicts,
  useConflictProposals,
  useResolveConflict,
  type OpenConflictRow,
  type ConflictProposalRow,
} from "@/hooks/useResearchHubCio";

// Some resolutions arrive as a tidy sentence already; others as an enum we translate
// to a plain recommendation. Never show the raw enum string.
function resolutionSentence(resolution: string | null | undefined): string {
  const key = (resolution ?? "").toString().toLowerCase().trim();
  switch (key) {
    case "keep_a":
    case "prefer_a":
    case "winner_a":
      return "Keep the first view, set the other aside.";
    case "keep_b":
    case "prefer_b":
    case "winner_b":
      return "Keep the second view, set the other aside.";
    case "keep_both":
    case "coexist":
      return "Let both views stand for now.";
    case "merge":
      return "Merge the two into a single view.";
    case "retire_both":
    case "drop_both":
      return "Retire both views.";
    case "needs_human":
    case "escalate":
      return "Too close to call by the numbers — settle this by hand.";
    case "":
      return "No recommendation on file.";
    default:
      // If it already reads like a sentence, keep it; otherwise humanise the enum.
      return /\s/.test((resolution ?? "").toString().trim())
        ? (resolution as string).toString().trim()
        : `${humanise(resolution)}.`;
  }
}

export default function CioConflicts() {
  const conflicts = useCioOpenConflicts();
  const proposals = useConflictProposals();

  // Join open_conflicts → proposals by conflict_id.
  const proposalByConflict = useMemo(() => {
    const map = new Map<string, ConflictProposalRow>();
    for (const p of proposals.data ?? []) {
      const id = p?.conflict_id;
      if (id) map.set(String(id), p);
    }
    return map;
  }, [proposals.data]);

  // Conflicts are the screen; proposals only add the suggested resolution. So gate the whole screen
  // on the conflicts query alone — a proposals failure degrades to "conflicts without
  // recommendations" rather than blanking everything.
  const isLoading = conflicts.isLoading;
  const error = conflicts.error;
  const proposalsFailed = !!proposals.error;

  const rows = (conflicts.data ?? []).filter(Boolean);

  return (
    <div className="space-y-6 max-w-3xl">
      <header className="space-y-1">
        <h1 className="font-heading text-xl font-semibold">Conflicts to settle</h1>
        <p className="font-body text-sm text-muted-foreground">
          Where two of our views pull against each other. Apply the suggested call, or set one aside by hand.
        </p>
      </header>

      {isLoading ? (
        <LoadingState rows={3} />
      ) : error ? (
        <ErrorState
          onRetry={() => {
            conflicts.refetch();
            proposals.refetch();
          }}
        />
      ) : rows.length === 0 ? (
        <EmptyState title="No open conflicts. Nicely settled." tone="good" />
      ) : (
        <div className="space-y-3">
          {proposalsFailed && (
            <button
              onClick={() => proposals.refetch()}
              className="w-full text-left rounded-md border border-dashed bg-muted/30 px-3 py-2 font-body text-xs text-muted-foreground hover:text-foreground"
            >
              Couldn't load the suggested resolutions just now — showing the conflicts only. Try again.
            </button>
          )}
          {rows.map((c: OpenConflictRow, i: number) => (
            <ConflictCard
              key={c.conflict_id ?? i}
              conflict={c}
              proposal={c.conflict_id ? proposalByConflict.get(String(c.conflict_id)) : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ConflictCard({
  conflict,
  proposal,
}: {
  conflict: OpenConflictRow;
  proposal?: ConflictProposalRow;
}) {
  const resolve = useResolveConflict();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");

  const a = conflict.a_label ?? "A view";
  const b = conflict.b_label ?? "Another view";
  const aGlyph = directionInfo(conflict.a_direction).glyph;
  const bGlyph = directionInfo(conflict.b_direction).glyph;
  const headline = `${a}${aGlyph ? ` ${aGlyph}` : ""} vs ${b}${bGlyph ? ` ${bGlyph}` : ""}`;

  const conflictId = conflict.conflict_id ? String(conflict.conflict_id) : null;
  const resolution = proposal?.proposed_resolution;
  const recommendation = resolutionSentence(resolution);
  const needsHuman = proposal?.needs_human === true;
  // Apply needs an id and a resolution to send. needs_human is allowed (just de-emphasised).
  const canApply = !!conflictId && !!resolution;

  const askPrefill = `Help me settle the conflict between "${a}" and "${b}".`;

  function confirmApply() {
    if (!conflictId || !resolution) return;
    resolve.mutate(
      { conflictId, resolution, note: note.trim() ? note.trim() : null },
      {
        onSuccess: () => {
          toast.success("Conflict settled.");
          setOpen(false);
          setNote("");
        },
        onError: () => {
          toast.error("We couldn't settle that just now. Please try again.");
        },
      },
    );
  }

  return (
    <Card className="p-4 space-y-3">
      {/* Headline: from/to pair + direction glyphs (the two labels can be identical). */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="font-heading text-base font-medium">
            {a}
            {aGlyph && <span className="ml-1">{aGlyph}</span>}{" "}
            <span className="font-body text-sm text-muted-foreground">vs</span>{" "}
            {b}
            {bGlyph && <span className="ml-1">{bGlyph}</span>}
          </div>
          <div className="font-body text-xs text-muted-foreground">{conflictKind(conflict.kind)}</div>
        </div>
        <AskAboutThis prefill={askPrefill} label="" className="shrink-0" />
      </div>

      {/* Suggested resolution from the matched proposal. */}
      {proposal ? (
        <div className={cn("space-y-2 rounded-md border bg-muted/30 p-3", needsHuman && "opacity-90")}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="font-body text-sm font-medium text-foreground">{recommendation}</p>
            {needsHuman && <AttentionBadge label="Too close to call — needs a person" tone="attention" />}
          </div>

          {proposal.margin != null && (
            <StrengthBar value={proposal.margin} label={marginBand(proposal.margin_band)} />
          )}

          {proposal.rationale && (
            <p className="font-body text-sm italic text-muted-foreground">"{proposal.rationale}"</p>
          )}
        </div>
      ) : (
        <p className="font-body text-sm text-muted-foreground">
          No suggested resolution yet — this one will need a closer look.
        </p>
      )}

      {/* Apply (the one CIO write). De-emphasised but still allowed when needs_human. */}
      <div className="flex items-center justify-end pt-1">
        <AlertDialog
          open={open}
          onOpenChange={(o) => {
            setOpen(o);
            if (!o) setNote(""); // reset the note whenever the dialog closes (cancel/escape/backdrop), not only on success
          }}
        >
          <Button
            variant={needsHuman ? "outline" : "default"}
            size="sm"
            disabled={!canApply || resolve.isPending}
            onClick={() => setOpen(true)}
          >
            Apply
          </Button>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading">Settle this conflict?</AlertDialogTitle>
              <AlertDialogDescription className="font-body space-y-2">
                <span className="block">{headline}</span>
                <span className="block text-foreground">{recommendation}</span>
                {needsHuman && (
                  <span className="block text-[hsl(var(--financial-gold))]">
                    The numbers say this is too close to call — apply only if you're confident.
                  </span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>

            <div className="space-y-1.5">
              <label className="font-body text-xs text-muted-foreground" htmlFor="conflict-note">
                Add a note (optional)
              </label>
              <Textarea
                id="conflict-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Why you're settling it this way…"
                rows={3}
                className="resize-none font-body text-sm"
              />
            </div>

            <AlertDialogFooter>
              <AlertDialogCancel disabled={resolve.isPending} className="font-body">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                className="font-body"
                disabled={resolve.isPending}
                onClick={(e) => {
                  e.preventDefault();
                  confirmApply();
                }}
              >
                {resolve.isPending ? "Settling…" : "Apply"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Card>
  );
}
